"use client";

import { useEffect, useState } from "react";
import { Icon, isIconName } from "@/lib/icons/Icon";
import type { UserAccountExtension } from "@/lib/api/user-account-extensions";
import styles from "./PluginSidebarBox.module.css";

/**
 * Wyciąga aktualny extension action_id z `window.location.hash`.
 * Identyczna logika jak `getExtensionActionFromHash` w `ProfileTabs.tsx` —
 * trzymane lokalnie żeby uniknąć cyclic import / shared state.
 */
function readActiveExtensionAction(): string | null {
  if (typeof window === "undefined") return null;
  const raw = (window.location.hash || "").replace(/^#/, "");
  if (raw === "") return null;
  for (const piece of raw.split(",")) {
    if (piece.startsWith("extension-")) return piece.slice("extension-".length);
  }
  return null;
}

/**
 * Sidebar boks dla pojedynczego pluginu rozszerzającego shell `c_user_accounts`.
 *
 * Renderowany w sidebar `ProfileTabs`, pod profile header card. Każdy plugin =
 * osobny boks z tytułem (nazwa pluginu) i listą linków (z `extension.actions`).
 *
 * **Plugin jako lista linków, NIE jako tab:** w odróżnieniu od core tabs
 * (Twoje dane / Hasło / Konto) plugin nie ma własnego "view" w main area —
 * każdy link akcji nawiguje do shell URL z `extension-{action}` extras.
 * Shell `c_user_accounts` (przyszła iteracja w react-cms) parsuje URL extras
 * i renderuje appropriate view (np. inbox listing, message preview).
 *
 * **Badge** — `sidebar_badge > 0` renderowany jako czerwony pill obok tytułu
 * (np. liczba nieprzeczytanych wiadomości).
 *
 * Layout:
 *   ┌──────────────────────────────┐
 *   │ 📩 Prywatne wiadomości   [3] │  ← tytuł + badge
 *   ├──────────────────────────────┤
 *   │ 📨 Skrzynka odbiorcza        │
 *   │ ✈️  Wiadomości wysłane        │
 *   │ ➕ Nowa wiadomość             │
 *   │ 📦 Archiwum                   │
 *   └──────────────────────────────┘
 */
export function PluginSidebarBox({
  extension,
  collapsed,
  disabled,
}: {
  extension: UserAccountExtension;
  collapsed: boolean;
  /** `true` -> wszystkie linki plugin nieklikalne + wygaszone. Używane gdy user
      ma pending MFA (required + !enabled) - musi skonfigurować MFA przed
      przejściem na jakąkolwiek stronę plugin'a. */
  disabled?: boolean;
}) {
  /* Action ids z plugin'a (kolejność insercji = kolejność renderowania). */
  const actionEntries = Object.entries(extension.actions);

  /* Invalid actions (odrzucone przez backend walidator `extension-` prefix).
     **Tylko dev mode** — w prod ignorowane (visitor nie widzi popsutych linków).
     Renderowane z warning visual + tooltip z reason kodem. */
  const isDev = process.env.NODE_ENV === "development";
  const invalidActions = isDev ? (extension.invalid_actions ?? []) : [];

  if (actionEntries.length === 0 && invalidActions.length === 0) return null;

  const badge = typeof extension.sidebar_badge === "number" && extension.sidebar_badge > 0
    ? extension.sidebar_badge
    : null;

  /* Aktywny link — match po href.includes('extension-{actionId}'). Reaguje
     na `hashchange` żeby zmienić wizualny active state bez przeładowania
     boksu (spójne z routing strategy: hash-only). */
  const [activeActionId, setActiveActionId] = useState<string | null>(null);
  useEffect(() => {
    setActiveActionId(readActiveExtensionAction());
    const onHashChange = () => setActiveActionId(readActiveExtensionAction());
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  /* Warunkowe klasy — collapsed mode wymaga innego paddingu / centrowania.
     `:global` w CSS module nie matche hashed class z `CUserAccounts.module.css`,
     więc rozszerzamy local class names per state. */
  const boxClass = collapsed ? `${styles.pluginBox} ${styles.pluginBoxCollapsed}` : styles.pluginBox;

  return (
    <section className={boxClass} data-plugin-symbol={extension.symbol}>
      {!collapsed && (
        <header className={styles.pluginBoxHeader}>
          <h3 className={styles.pluginBoxTitle}>{extension.name}</h3>
          {badge !== null && (
            <span className={styles.pluginBoxBadge} aria-label={`${badge} nieprzeczytanych`}>
              {badge > 99 ? "99+" : badge}
            </span>
          )}
        </header>
      )}
      {/* Collapsed: badge nad pierwszą ikoną jako mała kropka — sygnał "coś czeka"
          bez precyzyjnej liczby (oszczędność miejsca, bo header ukryty). */}
      {collapsed && badge !== null && (
        <span className={styles.pluginBoxBadgeDot} aria-label={`${badge} nieprzeczytanych`} />
      )}
      <ul className={styles.pluginBoxList}>
        {actionEntries.map(([actionId, action]) => {
          /* Active match po `extension-{X}` w href. Każdy plugin action
             ma unique X (np. `extension-received-messages`) -> comparison
             jednoznaczny. */
          const isActive = activeActionId !== null
            && action.href.includes(`extension-${activeActionId}`);
          const baseClass = collapsed
            ? `${styles.pluginBoxLink} ${styles.pluginBoxLinkCollapsed}`
            : styles.pluginBoxLink;
          //Kolejność klas: disabled ma priorytet nad active (user nie klika,
          //active state nie jest istotny). Semantycznie disabled = "nie da się
          //kliknąć", stąd wygląd jak wyłączony button (opacity + not-allowed).
          const linkClassFinal = disabled
            ? `${baseClass} ${styles.pluginBoxLinkDisabled}`
            : isActive
              ? `${baseClass} ${styles.pluginBoxLinkActive}`
              : baseClass;

          return (
            <li key={actionId} className={styles.pluginBoxItem}>
              {/* Plain `<a>` (NIE Next.js `<Link>`) dla hash-only navigation.
                  Backend ILINK zwraca pełen URL z origin (`https://.../pl/konto#X`)
                  dla SEO/bookmarkability — onClick interceptor wykrywa same-page
                  hash link i wymusza `window.location.hash =` które zawsze
                  fires `hashchange` event (ProfileTabs listener triggeruje
                  state update + re-render bez Server Component reload). */}
              <a
                //Brak href w disabled state - blokuje "otwórz w nowej karcie"
                //przez browser (Ctrl+klik) i visual signal (kursor default).
                href={disabled ? undefined : action.href}
                className={linkClassFinal}
                title={collapsed ? `${extension.name} — ${action.label}` : undefined}
                aria-current={isActive ? "page" : undefined}
                aria-disabled={disabled || undefined}
                onClick={(e) => {
                  if (disabled) {
                    e.preventDefault();
                    return;
                  }
                  /* Tylko dla "normal" click (bez Ctrl/Shift/middle) — pozwól
                     browserowi obsłużyć "open in new tab" itp. */
                  if (e.ctrlKey || e.metaKey || e.shiftKey || e.button !== 0) return;
                  try {
                    const url = new URL(action.href, window.location.origin);
                    if (
                      url.origin === window.location.origin
                      && url.pathname === window.location.pathname
                    ) {
                      /* Same-page hash nav — preventDefault + manual hash set.
                         `location.hash =` fires hashchange ZAWSZE (browser spec),
                         nawet gdy nowy hash to ten sam co poprzedni (force re-fire
                         dla "kliknij ten sam link ponownie"). */
                      e.preventDefault();
                      const newHash = url.hash || "";
                      if (window.location.hash === newHash) {
                        /* Force re-trigger: tymczasowo clear hash, potem set. */
                        window.location.hash = "";
                      }
                      window.location.hash = newHash;
                    }
                  } catch { /* malformed URL — fallback do default browser nav */ }
                }}
              >
                {isIconName(action.icon) && (
                  <Icon name={action.icon} className={styles.pluginBoxIcon} />
                )}
                {!collapsed && <span className={styles.pluginBoxLabel}>{action.label}</span>}
              </a>
            </li>
          );
        })}

        {/* Invalid actions — TYLKO w dev mode. Warning visual (czerwony border
            + ikona ostrzeżenia) + tooltip z reason. Klik NIE nawiguje (link
            disabled) — naprawienie wymaga zmiany w plugin `*.inc.php`. */}
        {invalidActions.map((inv) => {
          const reasonText = inv.reason === "missing_extension_prefix"
            ? `Action href musi zawierać prefix "extension-" w hash/extras. Aktualny href: "${inv.href}". Plugin ${extension.symbol} powinien używać ILINK_ANCHOR z prefix "extension-{action_id}".`
            : `Action data invalid (reason: ${inv.reason}).`;
          const baseClass = collapsed
            ? `${styles.pluginBoxLink} ${styles.pluginBoxLinkCollapsed}`
            : styles.pluginBoxLink;
          const invalidClassFinal = `${baseClass} ${styles.pluginBoxLinkInvalid}`;

          return (
            <li key={`invalid-${inv.key}`} className={styles.pluginBoxItem}>
              <a
                href="#"
                className={invalidClassFinal}
                title={`[DEV WARNING] ${reasonText}`}
                onClick={(e) => {
                  e.preventDefault();
                  // eslint-disable-next-line no-console
                  console.warn(
                    `[CUserAccounts plugin] Invalid action "${inv.key}" w plugin ${extension.symbol}: ${reasonText}`,
                  );
                }}
              >
                <Icon name="warning" className={styles.pluginBoxIcon} />
                {!collapsed && (
                  <span className={styles.pluginBoxLabel}>
                    {inv.label}
                    <span className={styles.pluginBoxInvalidBadge}>DEV: missing &quot;extension-&quot;</span>
                  </span>
                )}
              </a>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
